/**
 * CSRF Token Management
 * 
 * This utility handles CSRF token extraction, storage, and inclusion in API requests.
 * CSRF tokens are required for state-changing operations (POST, PUT, PATCH, DELETE)
 * to prevent Cross-Site Request Forgery attacks.
 */

// Storage key for CSRF token in sessionStorage
const CSRF_TOKEN_KEY = 'csrf_token';

// Cache for CSRF token with TTL
let csrfTokenCache: { token: string; expires: number } | null = null;

// CSRF token TTL (1 hour, matching backend configuration)
const CSRF_TOKEN_TTL = 60 * 60 * 1000; // 1 hour in milliseconds

/**
 * Extract CSRF token from response headers
 */
export const extractCsrfToken = (response: Response): string | null => {
  return response.headers.get('X-CSRF-Token');
};

/**
 * Store CSRF token in sessionStorage and cache
 */
export const storeCsrfToken = (token: string): void => {
  const expires = Date.now() + CSRF_TOKEN_TTL;
  
  // Store in sessionStorage
  sessionStorage.setItem(CSRF_TOKEN_KEY, JSON.stringify({ token, expires }));
  
  // Update cache
  csrfTokenCache = { token, expires };
};

/**
 * Get CSRF token from cache or sessionStorage
 */
export const getCsrfToken = (): string | null => {
  const now = Date.now();
  
  // Check cache first
  if (csrfTokenCache && csrfTokenCache.expires > now) {
    return csrfTokenCache.token;
  }
  
  // Check sessionStorage
  try {
    const stored = sessionStorage.getItem(CSRF_TOKEN_KEY);
    if (stored) {
      const { token, expires } = JSON.parse(stored);
      if (expires > now) {
        // Update cache
        csrfTokenCache = { token, expires };
        return token;
      } else {
        // Token expired, remove from storage
        sessionStorage.removeItem(CSRF_TOKEN_KEY);
        csrfTokenCache = null;
      }
    }
  } catch (error) {
    console.warn('Failed to parse stored CSRF token:', error);
    sessionStorage.removeItem(CSRF_TOKEN_KEY);
    csrfTokenCache = null;
  }
  
  return null;
};

/**
 * Clear CSRF token from storage and cache
 */
export const clearCsrfToken = (): void => {
  sessionStorage.removeItem(CSRF_TOKEN_KEY);
  csrfTokenCache = null;
};

/**
 * Check if a request method requires CSRF protection
 */
export const requiresCsrfToken = (method: string): boolean => {
  return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());
};

/**
 * Get CSRF token for a request, fetching a new one if needed
 */
export const getCsrfTokenForRequest = async (method: string): Promise<string | null> => {
  // Only state-changing methods require CSRF tokens
  if (!requiresCsrfToken(method)) {
    return null;
  }
  
  // Try to get existing token
  const existingToken = getCsrfToken();
  if (existingToken) {
    return existingToken;
  }
  
  // If no valid token exists, we need to fetch one
  // This will be handled by the authenticatedFetch function
  return null;
};

/**
 * Initialize CSRF token by making a GET request
 * This should be called when the app starts to ensure we have a valid token
 */
export const initializeCsrfToken = async (): Promise<void> => {
  try {
    // Use VITE_API_URL if available (production/deployed), otherwise use relative path (local dev with proxy)
    const baseUrl = import.meta.env.VITE_API_URL || '';
    const apiUrl = baseUrl ? `${baseUrl}/api/csrf/token` : '/api/csrf/token';
    
    // Make a GET request to the dedicated CSRF token endpoint
    const response = await fetch(apiUrl, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const token = extractCsrfToken(response);
      if (token) {
        storeCsrfToken(token);
      } else {
        console.warn('🔐 [CSRF Init] ⚠️ No token in response');
      }
    } else {
      console.error('🔐 [CSRF Init] ❌ Response not OK:', response.status, response.statusText);
    }
  } catch (error) {
    console.error('🔐 [CSRF Init] ❌ Failed to initialize CSRF token:', error);
  }
};
